import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Central registry of all LaunchDarkly feature flags used in the application.
 *
 * This is the single source of truth for flag names and types.
 * When adding a new flag:
 * 1. Add the property to this interface
 * 2. Add the default value to FLAG_DEFAULTS
 * 3. Use `useFeatureFlags()` in your component to read it
 */
export interface FeatureFlags {
  // Supplier routes
  createManualBrokeredBooking: boolean;
  partnerSubscriptionRedesign: boolean;
  supplierReadyForPayoutRedesign: boolean;
  supplierBookingsNavRedesign: boolean;
  supplierApiIntegration: boolean;
  supplierPublicProfile: boolean;
  manageLocationsRedesign: boolean;
  createLocationWizard: boolean;
  locationEnrichment: boolean;
  supplierCapacitySchedule: boolean;
  /** Shows the "Bulk Edit Locations" button on the Manage Locations page. */
  locationBulkEdit: boolean;

  // Gate management
  supplierInterchangeRedesign: boolean;
  supplierCurrentInventoryRedesign: boolean;
  supplierCheckInRedesign: boolean;
  supplierPendingInventoryActivity: boolean;
  mobileAppBanner: boolean;

  // Admin

  // Search
  optimizedLocationSearch: boolean;
  searchMapV2: boolean;
  cachedSupplierSearch: boolean;

  // Billing / tax
  collectTax: boolean;

  // Buyer
  buyerTextOptIn: boolean;

  // Assistant
  /**
   * Master switch for the Sentry assistant. With this off nobody sees it,
   * whatever the per-audience flags say.
   *
   * On its own this enables the assistant for **admins only**. Suppliers and
   * buyers each need their own flag as well, so a customer-facing audience can
   * be enabled or pulled without taking the whole feature down.
   */
  assistantChat: boolean;
  /** Offers the assistant to supplier accounts. Requires `assistantChat`. */
  assistantChatSuppliers: boolean;
  /** Offers the assistant to buyer accounts. Requires `assistantChat`. */
  assistantChatBuyers: boolean;

  // Telemetry
  faroTelemetry: boolean;
}

/**
 * Default values for all feature flags.
 * These apply when the flag hasn't loaded yet or doesn't exist in LaunchDarkly.
 */
const FLAG_DEFAULTS: FeatureFlags = {
  createManualBrokeredBooking: false,
  partnerSubscriptionRedesign: false,
  supplierReadyForPayoutRedesign: false,
  supplierBookingsNavRedesign: false,
  supplierApiIntegration: false,
  supplierPublicProfile: false,
  manageLocationsRedesign: false,
  createLocationWizard: false,
  locationEnrichment: false,
  supplierCapacitySchedule: false,
  locationBulkEdit: false,
  supplierInterchangeRedesign: false,
  supplierCurrentInventoryRedesign: false,
  supplierCheckInRedesign: false,
  supplierPendingInventoryActivity: false,
  mobileAppBanner: false,
  optimizedLocationSearch: false,
  searchMapV2: false,
  cachedSupplierSearch: false,
  collectTax: false,
  buyerTextOptIn: false,
  // The three assistant flags are registered in LaunchDarkly, so in normal
  // operation LD's values decide who sees the assistant and these defaults only
  // apply if the SDK fails to load. They are set to fail *closed* for customers:
  // the master stays on so a LaunchDarkly outage does not take the kill switch
  // away from staff/admins, while the two customer-facing flags default off so
  // the same outage cannot expose the assistant — and the paid backend agent it
  // enables — to suppliers or buyers.
  assistantChat: true,
  assistantChatSuppliers: false,
  assistantChatBuyers: false,
  faroTelemetry: false,
};

/**
 * Hook to access LaunchDarkly feature flags with centralized typing and defaults.
 *
 * Wraps the LD SDK's `useFlags()` with a single shared `FeatureFlags` interface
 * so all consumers get consistent types and safe defaults.
 *
 * @example
 * const { faroTelemetry, supplierApiIntegration } = useFeatureFlags();
 */
export function useFeatureFlags(): FeatureFlags {
  const flags = useFlags<Partial<FeatureFlags>>();
  return { ...FLAG_DEFAULTS, ...flags };
}
